/*
  Cart-level bundle oversell guard.

  Problem: a bundle product and its own component books share real inventory, but
  Shopify's native add-to-cart stock check only ever looks at ONE line item at a time —
  it has no idea a bundle and a book in the same cart draw from the same physical stock.
  That let a customer add a bundle AND separately add its own shared component past real
  stock, both succeeding, because neither add-to-cart call alone looked over the limit.

  Fix: intercept every /cart/add(.js|.json) call site sitewide (there are several,
  scattered across PDP/cart-drawer/bundle sections) at the network level by wrapping
  window.fetch once, here, instead of editing each call site individually. Before letting
  a cart/add request through, resolve the REAL demand it would create (expanding any
  bundle into its real component variants, via window.__dsBundleComponentMap — rendered
  server-side in theme.liquid from each bundle's `bundle.component_variant_ids` metafield),
  add it to whatever's already sitting in the cart, and check the total against a live
  stock lookup. If it would oversell, we fabricate a Shopify-shaped 422 error response
  instead of the real network call — every existing call site already knows how to
  surface a failed cart/add error, so this requires no changes to any of them.

  Fails open on every step: any parse error, unexpected shape, or network failure to the
  stock-check endpoint just lets the add through as if this script didn't exist. This
  guard is a UX improvement (fail fast, in the cart, like a normal Shopify stock error) —
  the Cart Transform Function at checkout remains the real, structural enforcement point.
*/
(function () {
  if (window.__dsBundleGuardInstalled) return;
  window.__dsBundleGuardInstalled = true;

  const STOCK_ENDPOINT = "https://darussalam-fbt-admin.vercel.app/api/bundle-stock";
  const originalFetch = window.fetch.bind(window);
  const bundleMap = window.__dsBundleComponentMap || {};
  const shopDomain = window.__dsShopDomain || (window.Shopify && window.Shopify.shop) || "";

  // Precompute the set of every real variant id that's a bundle component ANYWHERE on the
  // site, so we can cheaply decide "is this add even bundle-related?" without an API call
  // for the overwhelming majority of adds that have nothing to do with any bundle.
  const allComponentIds = new Set();
  Object.values(bundleMap).forEach((ids) => (ids || []).forEach((gid) => allComponentIds.add(gidToNumeric(gid))));

  function gidToNumeric(id) {
    if (id == null) return "";
    const s = String(id);
    const parts = s.split("/");
    return parts[parts.length - 1];
  }

  function isCartAddRequest(url, method) {
    if (!method || method.toUpperCase() !== "POST") return false;
    return /\/cart\/add(\.js|\.json)?(\?|$)/.test(url);
  }

  function parseItemsFromBody(rawBody) {
    if (!rawBody) return null;
    let parsed;
    try {
      parsed = typeof rawBody === "string" ? JSON.parse(rawBody) : rawBody;
    } catch (e) {
      return null;
    }
    if (Array.isArray(parsed.items)) {
      return parsed.items.map((it) => ({ id: String(it.id), quantity: Number(it.quantity) || 1 }));
    }
    if (parsed.id != null) {
      return [{ id: String(parsed.id), quantity: Number(parsed.quantity) || 1 }];
    }
    return null;
  }


  function showStockPopup(title, available) {
    if (document.getElementById("ds-stock-popup")) return;
    const msg = available > 0
      ? "Only <strong>" + available + "</strong> left in stock for <strong>" + esc(title) + "</strong>. Your cart already holds part of this stock (bundles and their books share the same copies), so we can’t add more."
      : "<strong>" + esc(title) + "</strong> has no more copies available right now. Bundles and their books share the same stock.";
    const wrap = document.createElement("div");
    wrap.id = "ds-stock-popup";
    wrap.setAttribute("role", "dialog");
    wrap.setAttribute("aria-modal", "true");
    wrap.setAttribute("aria-labelledby", "ds-stock-popup-h");
    wrap.innerHTML =
      "<style>" +
      "#ds-stock-popup{position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;padding:16px;background:rgba(15,20,18,.62);backdrop-filter:blur(3px);animation:dsspf .25s ease}" +
      "#ds-stock-popup .c{max-width:400px;width:100%;background:#fffdf8;border-radius:16px;padding:32px 28px 24px;text-align:center;box-shadow:0 24px 60px rgba(0,0,0,.35);border-top:4px solid #b8903a;animation:dssps .3s cubic-bezier(.2,.8,.2,1)}" +
      "#ds-stock-popup .i{width:54px;height:54px;margin:0 auto 14px;border-radius:50%;background:#f4ead2;color:#8a6a1f;display:flex;align-items:center;justify-content:center}" +
      "#ds-stock-popup h2{margin:0 0 10px;font:600 21px/1.25 Georgia,\"Cormorant\",serif;color:#1c2a24}" +
      "#ds-stock-popup p{margin:0 0 22px;font:400 15px/1.55 system-ui,sans-serif;color:#4a554f}" +
      "#ds-stock-popup .b{display:flex;gap:10px;justify-content:center;flex-wrap:wrap}" +
      "#ds-stock-popup a,#ds-stock-popup button{font:600 14px system-ui,sans-serif;padding:12px 22px;border-radius:999px;cursor:pointer;text-decoration:none;border:1.5px solid #1c2a24}" +
      "#ds-stock-popup button{background:#1c2a24;color:#fff}#ds-stock-popup a{background:transparent;color:#1c2a24}" +
      "#ds-stock-popup :focus-visible{outline:3px solid #b8903a;outline-offset:2px}" +
      "@keyframes dsspf{from{opacity:0}}@keyframes dssps{from{opacity:0;transform:translateY(14px) scale(.97)}}" +
      "@media(prefers-reduced-motion:reduce){#ds-stock-popup,#ds-stock-popup .c{animation:none}}" +
      "</style>" +
      "<div class=\"c\"><div class=\"i\" aria-hidden=\"true\"><svg width=\"26\" height=\"26\" viewBox=\"0 0 24 24\" fill=\"none\" stroke=\"currentColor\" stroke-width=\"2\" stroke-linecap=\"round\" stroke-linejoin=\"round\"><path d=\"M4 5h16v4H4zM6 9v10h12V9M10 13h4\"/></svg></div>" +
      "<h2 id=\"ds-stock-popup-h\">Limited stock</h2><p>" + msg + "</p>" +
      "<div class=\"b\"><button type=\"button\" data-x>Got it</button><a href=\"/cart\">View cart</a></div></div>";
    const close = () => { wrap.remove(); document.removeEventListener("keydown", onKey); };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    wrap.addEventListener("click", (e) => { if (e.target === wrap || e.target.hasAttribute("data-x")) close(); });
    document.addEventListener("keydown", onKey);
    document.body.appendChild(wrap);
    wrap.querySelector("[data-x]").focus();
  }
  function esc(t) { return String(t).replace(/[&<>\"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" }[c])); }

  function bundleErrorResponse(title, available) {
    const description =
      available > 0
        ? `Only ${available} of "${title}" left in stock right now — this order would need more than that (including what's already in your cart).`
        : `"${title}" is out of stock right now.`;
    const body = JSON.stringify({ status: 422, message: "Cart Error", description });
    return new Response(body, {
      status: 422,
      statusText: "Unprocessable Entity",
      headers: { "Content-Type": "application/json; charset=utf-8" },
    });
  }

  async function getCurrentCartLines() {
    try {
      const res = await originalFetch("/cart.js", { headers: { Accept: "application/json" } });
      if (!res.ok) return [];
      const cart = await res.json();
      return (cart.items || []).map((it) => ({ id: String(it.variant_id ?? it.id), quantity: it.quantity }));
    } catch (e) {
      return [];
    }
  }

  function expandDemand(lines, demand) {
    lines.forEach(({ id, quantity }) => {
      const components = bundleMap[id];
      if (components && components.length > 0) {
        components.forEach((gid) => {
          const cid = gidToNumeric(gid);
          demand[cid] = (demand[cid] || 0) + quantity;
        });
      } else {
        demand[id] = (demand[id] || 0) + quantity;
      }
    });
  }

  function touchedRealIds(lines) {
    const set = new Set();
    lines.forEach(({ id }) => {
      const components = bundleMap[id];
      if (components && components.length > 0) {
        components.forEach((gid) => set.add(gidToNumeric(gid)));
      } else {
        set.add(id);
      }
    });
    return Array.from(set);
  }

  function isBundleRelated(newItems, existingLines) {
    const relevant = (line) => bundleMap[line.id] || allComponentIds.has(line.id);
    return newItems.some(relevant) || existingLines.some(relevant);
  }

  window.fetch = async function (input, init) {
    const url = typeof input === "string" ? input : input && input.url ? input.url : "";
    const method = (init && init.method) || (typeof input !== "string" && input && input.method) || "GET";

    if (!isCartAddRequest(url, method) || !shopDomain) {
      return originalFetch(input, init);
    }

    try {
      const rawBody = init && init.body;
      const newItems = parseItemsFromBody(rawBody);
      if (!newItems || newItems.length === 0) return originalFetch(input, init);

      const existingLines = await getCurrentCartLines();
      if (!isBundleRelated(newItems, existingLines)) {
        return originalFetch(input, init);
      }

      const demand = {};
      expandDemand(existingLines, demand);
      expandDemand(newItems, demand);

      const touched = touchedRealIds(newItems);
      if (touched.length === 0) return originalFetch(input, init);

      const stockRes = await originalFetch(STOCK_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shop_domain: shopDomain, variant_ids: touched }),
      });
      if (!stockRes.ok) return originalFetch(input, init);
      const stockJson = await stockRes.json();
      const stock = stockJson.stock || {};

      for (const id of touched) {
        const info = stock[id];
        if (!info || !info.tracked || info.policy !== "DENY") continue;
        const required = demand[id] || 0;
        if (required > info.available) {
          showStockPopup(info.title || "This item", info.available);
          return bundleErrorResponse(info.title || "This item", info.available);
        }
      }

      return originalFetch(input, init);
    } catch (e) {
      return originalFetch(input, init);
    }
  };
})();
